import { lazy } from 'react'
import type { ComponentType } from 'react'
import type { GameId, GameTemplate, ItemType, PracticeMode, RoundProps } from '../types'

/**
 * Registre des seize mécaniques (SPEC §5), à import différé.
 *
 * Aucune mécanique n'existe encore : chaque entrée déclare son `GameId`, les
 * types d'items qu'elle consomme et son mode, et pointe vers un composant qui
 * n'a pas encore été déposé — `games/<id>/Round.tsx`, export par défaut. Un lot
 * suivant n'a qu'à créer ce fichier pour que la mécanique devienne jouable,
 * sans toucher à ce registre ni à aucune autre mécanique.
 *
 * `import()` est appelé avec un gabarit interpolé (`` `./${id}/Round.tsx` ``),
 * pas un littéral : TypeScript ne tente donc pas de résoudre le module au
 * moment de la vérification de types (le dossier n'existe pas encore), et
 * Vite garde la capacité d'analyser l'import dynamique pour le découpage de
 * bundle une fois les fichiers déposés. C'est ce report qui garantit qu'aucune
 * mécanique ne se retrouve dans le bundle initial.
 *
 * L'extension `.tsx` doit rester explicite dans la partie statique du
 * gabarit : sans elle, `vite-dynamic-import-vars` refuse l'import (« A file
 * extension must be included in the static part of the import ») et
 * n'émet jamais le helper de découpage — aucune mécanique ne se chargerait
 * jamais, ni aujourd'hui ni quand les lots suivants déposeront leur
 * `games/<id>/Round.tsx`. `tsc` reste indifférent : il ne résout pas les
 * gabarits interpolés, avec ou sans extension.
 */

interface GameDefinition {
  id: GameId
  itemTypes: ItemType[]
  mode: PracticeMode
  /** Durée totale en ms — uniquement quand la SPEC §5 fixe une durée pour ce mode chrono. */
  durationMs?: number
}

function defineGame({ id, itemTypes, mode, durationMs }: GameDefinition): GameTemplate {
  return {
    id,
    itemTypes,
    mode,
    durationMs,
    Round: lazy(
      () => import(`./${id}/Round.tsx`) as Promise<{ default: ComponentType<RoundProps> }>,
    ),
  }
}

/** Mécaniques × types d'items × mode, tels que fixés par SPEC.md §5. */
export const GAMES: Record<GameId, GameTemplate> = {
  sprint: defineGame({ id: 'sprint', itemTypes: ['qcm', 'vrai_faux'], mode: 'chrono', durationMs: 60_000 }),
  vrai_faux_express: defineGame({ id: 'vrai_faux_express', itemTypes: ['vrai_faux'], mode: 'chrono', durationMs: 45_000 }),
  cas_pratique: defineGame({ id: 'cas_pratique', itemTypes: ['qcm'], mode: 'manches' }),
  intrus: defineGame({ id: 'intrus', itemTypes: ['qcm'], mode: 'manches' }),
  appariement: defineGame({ id: 'appariement', itemTypes: ['paire'], mode: 'chrono', durationMs: 60_000 }),
  memory_definitions: defineGame({ id: 'memory_definitions', itemTypes: ['paire'], mode: 'manches' }),
  mot_juste: defineGame({ id: 'mot_juste', itemTypes: ['paire'], mode: 'chrono' }),
  frise: defineGame({ id: 'frise', itemTypes: ['ordre'], mode: 'manches' }),
  chaine_causale: defineGame({ id: 'chaine_causale', itemTypes: ['ordre'], mode: 'manches' }),
  hierarchie: defineGame({ id: 'hierarchie', itemTypes: ['ordre'], mode: 'manches' }),
  carte_muette: defineGame({ id: 'carte_muette', itemTypes: ['localisation'], mode: 'chrono' }),
  schema_etiquete: defineGame({ id: 'schema_etiquete', itemTypes: ['localisation'], mode: 'manches' }),
  calcul_applique: defineGame({ id: 'calcul_applique', itemTypes: ['numerique'], mode: 'manches' }),
  estimation: defineGame({ id: 'estimation', itemTypes: ['numerique'], mode: 'chrono' }),
  tri_categoriel: defineGame({ id: 'tri_categoriel', itemTypes: ['classement'], mode: 'manches' }),
  texte_a_trous: defineGame({ id: 'texte_a_trous', itemTypes: ['texte_a_trous'], mode: 'manches' }),
}
